import React, { useEffect } from "react";
import {
  Navbar,
  PostsList,
  SinglePost,
  ErrorMessage,
  LandingPagesList,
} from "./Components";
import {
  useRouter,
  usePages,
  usePosts,
  usePost,
  useLandingPages,
} from "./hooks";
import { useLanguage } from "./LanguageContext";
import { useSmoothLoading } from "./useSmoothLoading";

// Simple loading component
const LoadingSpinner: React.FC<{ message?: string }> = ({ message }) => {
  const { t } = useLanguage();
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-center">
      <div className="animate-spin inline-block w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full mb-4"></div>
      <p className="text-gray-600 text-sm">{message || t("loading")}</p>
    </div>
  );
};

const App: React.FC = () => {
  const { locale } = useLanguage();
  const { currentRoute, navigate, navigateBack } = useRouter();
  const {
    pages,
    homePage,
    loading: pagesLoading,
    error: pagesError,
  } = usePages(locale);

  // Use smooth loading for pages first
  const pagesState = useSmoothLoading(pages, pagesLoading, 200);

  // Determine if current route is a page or post
  const currentSlug = "slug" in currentRoute ? currentRoute.slug : undefined;
  const isCurrentRoutePage = currentSlug
    ? (pagesState.displayData || []).some((page) => page.slug === currentSlug)
    : false;
  const actualRouteType =
    currentRoute.type === "page" && !isCurrentRoutePage && currentSlug
      ? "post"
      : currentRoute.type;

  const {
    posts,
    loading: postsLoading,
    error: postsError,
  } = usePosts(actualRouteType === "page" ? currentSlug : undefined, locale);
  const {
    post,
    loading: postLoading,
    error: postError,
  } = usePost(actualRouteType === "post" ? currentSlug : undefined, locale);
  const {
    landingPages,
    loading: landingLoading,
    error: landingError,
  } = useLandingPages(
    actualRouteType === "page" ? currentSlug : undefined,
    locale
  );

  // Use smooth loading for posts and individual post
  const postsState = useSmoothLoading(posts, postsLoading, 250);
  const postState = useSmoothLoading(post, postLoading, 250);
  const landingState = useSmoothLoading(landingPages, landingLoading, 250);

  // Auto-redirect to home page if on root
  useEffect(() => {
    if (currentRoute.type === "home" && homePage) {
      navigate("page", homePage.slug);
    }
  }, [currentRoute.type, homePage, navigate]);

  // Show loading for initial page load or when we don't have pages yet
  if (pagesState.showLoading) {
    return (
      <div className="min-h-screen bg-white">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error if pages failed to load
  if (pagesError) {
    return (
      <div className="min-h-screen bg-white">
        <ErrorMessage message={pagesError} />
      </div>
    );
  }

  // If we've loaded once but have no pages, show error
  if (pagesState.hasLoadedOnce && !pagesState.displayData?.length) {
    return (
      <div className="min-h-screen bg-white">
        <ErrorMessage message="No pages found" />
      </div>
    );
  }

  // On first load, if we have pages but are still loading content, show loading
  if (!pagesState.hasLoadedOnce || !pagesState.displayData?.length) {
    return (
      <div className="min-h-screen bg-white">
        <LoadingSpinner />
      </div>
    );
  }

  const currentPageInfo = pagesState.displayData?.find(
    (page) => page.slug === currentSlug
  );

  // Show full screen loading until all initial content is ready
  const isInitialContentLoading =
    actualRouteType === "page" &&
    currentPageInfo &&
    (!postsState.hasLoadedOnce || !landingState.hasLoadedOnce);

  if (isInitialContentLoading) {
    return (
      <div className="min-h-screen bg-white">
        <LoadingSpinner />
      </div>
    );
  }

  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header
        className="relative bg-cover bg-center  w-full "
        style={{ backgroundImage: "url(/backround-01.jpg)" }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-black/40"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex flex-col md:flex-row items-start md:items-start gap-6 md:gap-4">
            <img
              src="/logo.svg"
              alt="Taidekansallispuisto Logo"
              className="w-full h-24  md:w-20 md:h-24 drop-shadow-lg filter  invert opacity-90  "
            />
            <div className="space-y-3 sm:space-y-4 flex-1 opacity-90 md:pt-4">
              <h1 className="text-2xl sm:text-3xl md:text-4xl  text-white leading-tight tracking-tight drop-shadow-lg whitespace-pre-line">
                <div className="font-corbel font-medium ">
                  {t("landing.title1")}
                </div>
                <div className="font-abeezee font-bold">
                  {t("landing.title2")}
                </div>
              </h1>
              <div className="max-w-lg sm:max-w-xl">
                <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                  {t("landing.subtitle")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
      <nav className="sticky top-0 bg-white border-b border-gray-200 z-50 ">
        <div className="max-w-4xl mx-auto px-6">
          <Navbar
            pages={pagesState.displayData || []}
            currentPage={currentSlug || null}
            onPageChange={(slug) => navigate("page", slug)}
          />
        </div>
      </nav>
      <main
        className="bg-repeat bg-fixed py-12 flex-1"
        style={{ backgroundImage: "url(/bg-clay3.jpg)" }}
      >
        <div className="max-w-4xl mx-auto  ">
          {actualRouteType === "post" && currentSlug ? (
            <>
              {postState.showLoading && <LoadingSpinner />}
              {postError && <ErrorMessage message={postError} />}
              {postState.displayData && (
                <SinglePost
                  post={postState.displayData}
                  onBackClick={() => homePage && navigateBack(homePage)}
                />
              )}
            </>
          ) : actualRouteType === "page" && currentPageInfo ? (
            <>
              {/* Landing Pages */}
              {landingState.showLoading && <LoadingSpinner />}
              {landingError && <ErrorMessage message={landingError} />}
              {(!landingState.isLoading || landingState.hasLoadedOnce) &&
                landingState.displayData &&
                landingState.displayData.length > 0 && (
                  <LandingPagesList landingPages={landingState.displayData} />
                )}

              {/* Posts */}
              {postsState.showLoading && <LoadingSpinner />}
              {postsError && <ErrorMessage message={postsError} />}
              {(!postsState.isLoading || postsState.hasLoadedOnce) &&
                postsState.displayData && (
                  <PostsList
                    postsGroup={postsState.displayData}
                    // desc={currentPageInfo.description}
                    onPostClick={(slug: string) =>
                      navigate("post", slug, {
                        type: "page",
                        slug: currentPageInfo.slug,
                      })
                    }
                    postsLanding={landingState.displayData || []}
                    currentPageSlug={currentPageInfo.slug}
                  />
                )}
            </>
          ) : pagesState.hasLoadedOnce ? (
            <ErrorMessage message="Page not found" />
          ) : (
            <LoadingSpinner />
          )}
        </div>
        <div className="pt-28" />
      </main>
      <footer className="bg-white ">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="space-y-4 text-sm text-shadyLeaf">
            <p>
              Taidekansallispuistoa ovat suunnittelemassa useat paikalliset
              toimijat ja yksityiset henkilöt.
            </p>
            <p>
              Ota yhteyttä:{" "}
              <span className="text-geraniumSylvaticum">
                taidekansallispuisto@gmail.com
              </span>{" "}
              tai Laura Mellanen (koordinaattori, Vaara-Kainuun
              taidekansallispuiston valmisteluhanke)
              <span className="text-geraniumSylvaticum">
                {" "}
                laura.mellanen@mustarinda.fi
              </span>
              , 044 901 3080
            </p>

            <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
              <div className="text-xs text-gray-500 ">
                {`© ${new Date().getFullYear()} Vaara-Kainuun Taidekansallispuisto`}
              </div>
              <div className="flex items-center gap-4  text-geraniumSylvaticum">
                <a
                  href="https://www.facebook.com/taidekansallispuisto"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="flex items-center gap-2 hover:text-geraniumSylvaticum transition-colors font-medium"
                >
                  <span className="flex items-center justify-center w-7 h-7">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="inline align-middle"
                    >
                      <path
                        d="M17 2H7C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h5v-7H9v-3h3V9.5C12 7.57 13.57 6 15.5 6H18v3h-2.5C15.22 9 15 9.22 15 9.5V12h3l-.5 3H15v7h2c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <span className="text-base hover:underline hidden sm:inline">Facebook</span>
                </a>
                <a
                  href="https://www.instagram.com/taidekansallispuisto/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="flex items-center gap-2 hover:text-geraniumSylvaticum transition-colors font-medium"
                >
                  <span className="flex items-center justify-center w-7 h-7">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="inline align-middle"
                    >
                      <rect
                        x="2"
                        y="2"
                        width="20"
                        height="20"
                        rx="5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle cx="17" cy="7" r="1.2" fill="currentColor" />
                    </svg>
                  </span>
                  <span className="text-base hover:underline hidden sm:inline">Instagram</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
